/**
 * Known third-party RUM noise filters.
 *
 * Recurring error classes that are not actionable from application code but
 * flood the RUM error stream and page on-call as a P1 (1-month OpenObserve
 * inventory, 2026-10):
 *
 * - Google's reCAPTCHA runtime periodically throws
 *   `TypeError: Cannot read properties of undefined (reading 'nc')` from its
 *   own internal timers on long-lived or backgrounded pages
 *   (specs/bugs/BUG-2026-10-30T124816-recaptcha-nc-typeerror-rum-noise.md).
 * - Browser wallet extensions inject `window.ethereum` into the page context
 *   and throw a WebKit-style TypeError when their evaluation races or is
 *   blocked; MetaMask additionally logs "Failed to connect to MetaMask".
 * - Legacy IE/EdgeHTML-scripting-engine sessions (embedded webviews, bots)
 *   spam `Object Not Found Matching Id:1, MethodName:update, ParamCount:4`
 *   with no usable stack.
 * - `Script error.` is the browser-masked cross-origin error — unactionable
 *   by definition (the real error lives in the other origin's console).
 * - RUM SDK / reCAPTCHA telemetry intake delivery failures
 *   (`Fetch error POST ...openobserve.../rum/v1/live/...`,
 *   `Fetch error POST ...google.com/recaptcha/api2/...`) — the SDK already
 *   retries; these rows also embed the public RUM client token, so dropping
 *   them is log hygiene.
 *
 * Genuine application errors (AxiosError 4xx/5xx, BPOINT script-load
 * failures, payment XHR failures) are deliberately NOT matched here — they
 * are actionable signal.
 *
 * These predicates let the RUM `beforeSend` hooks drop that noise at the
 * source.
 */

const RECAPTCHA_NC_MESSAGE = "Cannot read properties of undefined (reading 'nc')";

const ETHEREUM_WALLET_MESSAGE =
  "undefined is not an object (evaluating 'window.ethereum.selectedAddress = undefined')";

const METAMASK_CONNECT_MESSAGE = "Failed to connect to MetaMask";

const CROSS_ORIGIN_SCRIPT_MESSAGE = '"Script error."';

/**
 * Signatures safe to match on message alone in BOTH the RUM error stream and
 * forwarded logs: none of them can originate from application code (app code
 * never touches window.ethereum, never emits the legacy scripting-engine
 * error, and "Script error." is browser-masked by definition).
 */
const MESSAGE_ONLY_NOISE = [
  ETHEREUM_WALLET_MESSAGE,
  METAMASK_CONNECT_MESSAGE,
  CROSS_ORIGIN_SCRIPT_MESSAGE,
];

const matchesMessageOnlyNoise = (message: string): boolean =>
  MESSAGE_ONLY_NOISE.some((signature) => message.includes(signature)) ||
  MESSAGE_ONLY_NOISE_PATTERNS.some((pattern) => pattern.test(message));

/**
 * Parameterized signatures safe to match by regex in BOTH the RUM error
 * stream and forwarded logs. App code cannot emit these (the legacy
 * IE/EdgeHTML scripting-engine error only comes from embedded webviews/bots).
 * Id and ParamCount vary per event, so they are captured, not literal.
 */
const MESSAGE_ONLY_NOISE_PATTERNS = [
  /Object Not Found Matching Id:\d+, MethodName:[A-Za-z]+, ParamCount:\d+/,
];

/**
 * Console-error prefixes that only appear in the forwarded logs stream
 * (intake delivery failures). Prefix match because the URLs carry unique
 * per-request query params.
 */
const LOG_ONLY_NOISE_PREFIXES = [
  "Fetch error POST https://www.google.com/recaptcha/api2/",
  "Fetch error POST https://openobserve.admin-infra.aldrinerp.com/rum/v1/live/",
];

/**
 * True for the reCAPTCHA-internal 'nc' TypeError captured as a RUM error
 * event. Matches on the message AND the gstatic reCAPTCHA stack so genuine
 * application TypeErrors with a similar message are never dropped.
 */
export const isRecaptchaNcTypeError = (
  message?: string,
  stack?: string
): boolean => {
  if (!message || !stack) {
    return false;
  }
  return (
    message.includes(RECAPTCHA_NC_MESSAGE) &&
    stack.includes("gstatic.com/recaptcha")
  );
};

/**
 * True for errors injected by browser wallet extensions (MetaMask et al.
 * evaluate `window.ethereum.selectedAddress` in the page context; when
 * injection is blocked or races, the evaluation throws this WebKit-style
 * TypeError). Application code never touches `window.ethereum`
 * (grep-verified), so message-only matching cannot mask a genuine app error.
 */
export const isWalletExtensionNoise = (message?: string): boolean =>
  !!message && message.includes(ETHEREUM_WALLET_MESSAGE);

/**
 * True for any known third-party noise captured as a RUM error event:
 * the stack-verified reCAPTCHA 'nc' TypeError, or a signature that cannot
 * originate from application code (matched message-only).
 */
export const isKnownThirdPartyRumError = (
  message?: string,
  stack?: string
): boolean =>
  isRecaptchaNcTypeError(message, stack) ||
  (!!message && matchesMessageOnlyNoise(message));

/**
 * True for forwarded console/log messages carrying known third-party noise:
 * any RUM-stream signature (message-only), or an intake-delivery failure
 * prefix. Log forwarding does not carry a stack.
 */
export const isKnownThirdPartyLogNoise = (message?: string): boolean =>
  !!message &&
  (message.includes(RECAPTCHA_NC_MESSAGE) ||
    matchesMessageOnlyNoise(message) ||
    LOG_ONLY_NOISE_PREFIXES.some((prefix) => message.startsWith(prefix)));
